(() => {
  if (window.CLCartRewardEffects) return;

  const controllers = new Map();
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const particleCounts = [9, 16, 25];
  const palettes = [
    ['#014438', '#538777', '#fff9e8'],
    ['#014438', '#538777', '#bca26a', '#f3dfac'],
    ['#014438', '#538777', '#bca26a', '#ef933b', '#fff1ca']
  ];

  const curve = (t, a, b) => 3 * (1 - t) ** 2 * t * a + 3 * (1 - t) * t ** 2 * b + t ** 3;
  const arrivalTime = fraction => {
    let low = 0;
    let high = 1;
    for (let step = 0; step < 16; step += 1) {
      const middle = (low + high) / 2;
      if (curve(middle, .61, 1) < fraction) low = middle;
      else high = middle;
    }
    return curve((low + high) / 2, .22, .36);
  };

  class RewardEffects {
    constructor(root) {
      this.root = root;
      this.bar = root.querySelector('[data-cl-shipping-bar]');
      this.track = root.querySelector('[data-cl-shipping-track]');
      this.fill = root.querySelector('[data-cl-shipping-fill]');
      this.impulse = root.querySelector('.cl-reward-orb__impulse');
      if (this.impulse && !this.impulse.querySelector('.cl-reward-orb__body')) {
        const body = document.createElement('span');
        body.className = 'cl-reward-orb__body';
        body.append(...this.impulse.childNodes);
        const embers = document.createElement('span');
        embers.className = 'cl-reward-embers';
        embers.append(document.createElement('i'), document.createElement('i'));
        this.impulse.append(body, embers);
      }
      this.layer = root.querySelector('[data-cl-reward-effects]');
      this.snapshot = null;
      this.pending = new Map();
      this.timers = new Set();
    }

    get reduced() {
      return motionQuery.matches || this.bar.dataset.clRewardMotion === 'false';
    }

    later(callback, delay) {
      const timer = window.setTimeout(() => {
        this.timers.delete(timer);
        if (this.root.isConnected) callback();
      }, delay);
      this.timers.add(timer);
      return timer;
    }

    cancel(timer) {
      window.clearTimeout(timer);
      this.timers.delete(timer);
    }

    clearEffects() {
      this.timers.forEach(timer => window.clearTimeout(timer));
      this.timers.clear();
      this.pending.clear();
      this.layer.replaceChildren();
      this.impulse?.querySelectorAll('.cl-reward-boost-sparks').forEach(sparks => sparks.remove());
      this.bar.classList.remove('is-boosted', 'is-success');
      this.root.querySelectorAll('.cl-cart-progress__milestone.is-bursting').forEach(marker => {
        marker.classList.remove('is-bursting');
      });
    }

    update(snapshot) {
      if (!this.bar || !this.track || !this.fill || !this.layer) return;
      const previous = this.snapshot;
      const signature = JSON.stringify(snapshot.rewards.map(reward => [reward.id, reward.target, reward.position]));
      const changedTargets = signature !== this.signature;
      const increased = previous && snapshot.subtotal > previous.subtotal;
      const decreased = previous && snapshot.subtotal < previous.subtotal;
      const changedProgress = previous && snapshot.progress !== previous.progress;
      this.previousSubtotal = previous?.subtotal ?? snapshot.subtotal;
      this.previousProgress = previous?.progress ?? snapshot.progress;
      this.snapshot = snapshot;
      this.signature = signature;

      const nextIndex = snapshot.rewards.findIndex(reward => snapshot.subtotal < reward.target);
      const segmentStart = nextIndex > 0 ? snapshot.rewards[nextIndex - 1].target : 0;
      const segmentEnd = nextIndex >= 0 ? snapshot.rewards[nextIndex].target : segmentStart;
      const segmentProgress = segmentEnd > segmentStart
        ? (snapshot.subtotal - segmentStart) / (segmentEnd - segmentStart)
        : 1;
      this.bar.dataset.clRewardEnergy = nextIndex < 0 ? 'complete'
        : segmentProgress >= .95 ? 'frenzy' : segmentProgress >= .85 ? 'critical' : segmentProgress >= .6 ? 'charged' : 'calm';
      this.bar.classList.toggle('is-empty', snapshot.subtotal <= 0);
      const nextRewardId = nextIndex >= 0 ? String(snapshot.rewards[nextIndex].id) : null;
      this.root.querySelectorAll('.cl-cart-progress__milestone[data-cl-reward-id]').forEach(marker => {
        marker.classList.toggle('is-next-reward', marker.dataset.clRewardId === nextRewardId && !marker.classList.contains('is-reached'));
      });

      if (!previous || changedTargets || decreased || this.reduced) this.clearEffects();

      let visualProgress = this.previousProgress;
      if (increased && changedProgress && !this.reduced && !changedTargets) {
        const trackWidth = this.track.getBoundingClientRect().width;
        if (trackWidth > 0) visualProgress = this.fill.getBoundingClientRect().width / trackWidth * 100;
      }
      this.fill.style.width = `${snapshot.progress}%`;

      if (!previous || changedTargets || this.reduced || !increased) return;

      this.bar.classList.add('is-boosted');
      (this.impulse?.getAnimations?.() || []).forEach(animation => {
        if (animation.animationName === 'cl-reward-boost') animation.currentTime = 0;
      });
      this.cancel(this.boostTimer);
      this.boostTimer = this.later(() => this.bar.classList.remove('is-boosted'), 800);
      this.boostSparks();

      if (!changedProgress) return;

      const crossed = snapshot.rewards.filter(reward => previous.subtotal < reward.target && snapshot.subtotal >= reward.target);
      const waiting = [...this.pending.values()].map(entry => entry.reward);
      this.pending.forEach(entry => this.cancel(entry.timer));
      this.pending.clear();
      const rewardsToBurst = new Map([...waiting, ...crossed].map(reward => [reward.id, reward]));
      const transition = (this.fill.getAnimations?.() || []).find(animation => animation.transitionProperty === 'width');
      const timing = transition?.effect?.getComputedTiming();
      const durationString = window.getComputedStyle(this.fill).transitionDuration;
      const declaredDuration = durationString.endsWith('ms') ? parseFloat(durationString) : parseFloat(durationString) * 1000;
      const duration = typeof timing?.duration === 'number' ? timing.duration : declaredDuration;
      const elapsed = Number(transition?.currentTime) || 0;

      rewardsToBurst.forEach(reward => {
        const distance = snapshot.progress - visualProgress;
        const fraction = distance > 0 ? Math.min(1, Math.max(0, (reward.position - visualProgress) / distance)) : 0;
        const delay = Math.max(0, (Number.isFinite(duration) ? duration : 700) * arrivalTime(fraction) - elapsed);
        const level = Math.min(3, snapshot.rewards.findIndex(item => item.id === reward.id) + 1);
        const timer = this.later(() => {
          this.pending.delete(reward.id);
          if (!this.reduced && this.snapshot.subtotal >= reward.target) this.burst(reward, level);
        }, delay);
        this.pending.set(reward.id, { reward, timer });
      });
    }

    boostSparks() {
      if (!this.impulse) return;
      this.impulse.querySelectorAll('.cl-reward-boost-sparks').forEach(sparks => sparks.remove());
      const sparks = document.createElement('span');
      sparks.className = 'cl-reward-boost-sparks';
      for (let index = 0; index < 3; index += 1) {
        const spark = document.createElement('i');
        spark.className = 'cl-reward-spark';
        spark.style.setProperty('--cl-particle-x', `${-18 - index * 7}px`);
        spark.style.setProperty('--cl-particle-y', `${(index - 1) * 6}px`);
        spark.style.setProperty('--cl-particle-delay', `${105 + index * 35}ms`);
        spark.style.setProperty('--cl-particle-rotation', `${(index - 1) * 18}deg`);
        sparks.appendChild(spark);
      }
      this.impulse.appendChild(sparks);
      this.later(() => sparks.remove(), 800);
    }

    burst(reward, level) {
      const burst = document.createElement('span');
      burst.className = 'cl-reward-burst';
      burst.dataset.level = String(level);
      burst.style.setProperty('--cl-burst-position', `${reward.position}%`);
      const colors = palettes[level - 1];
      const count = particleCounts[level - 1];
      const radius = [24, 36, 48][level - 1];
      const flash = document.createElement('span');
      flash.className = 'cl-reward-flash';
      const ring = document.createElement('span');
      ring.className = 'cl-reward-ring';
      burst.append(flash, ring);

      for (let index = 0; index < count; index += 1) {
        const angle = index / count * Math.PI * 2 + Math.random() * .22;
        const distance = radius * (.55 + Math.random() * .45);
        const particle = document.createElement('i');
        particle.className = 'cl-reward-particle';
        particle.style.setProperty('--cl-particle-x', `${Math.cos(angle) * distance}px`);
        particle.style.setProperty('--cl-particle-y', `${Math.sin(angle) * distance * .65}px`);
        particle.style.setProperty('--cl-particle-color', colors[index % colors.length]);
        particle.style.setProperty('--cl-particle-size', `${index % 3 === 0 ? 3.5 : 2.5}px`);
        particle.style.setProperty('--cl-particle-delay', `${index % 4 * 12}ms`);
        particle.style.setProperty('--cl-particle-rotation', `${angle * 180 / Math.PI}deg`);
        burst.appendChild(particle);
      }

      for (let index = 0; index < level + 2; index += 1) {
        const angle = (index / (level + 2) * Math.PI * 2) - .55;
        const spark = document.createElement('i');
        spark.className = 'cl-reward-spark';
        spark.style.setProperty('--cl-particle-x', `${Math.cos(angle) * radius * 1.12}px`);
        spark.style.setProperty('--cl-particle-y', `${Math.sin(angle) * radius * .62}px`);
        spark.style.setProperty('--cl-particle-delay', `${index % 2 * 16}ms`);
        spark.style.setProperty('--cl-particle-rotation', `${angle * 180 / Math.PI}deg`);
        burst.appendChild(spark);
      }

      if (level === 3) {
        this.bar.classList.add('is-success');
        this.cancel(this.successTimer);
        this.successTimer = this.later(() => this.bar.classList.remove('is-success'), 420);
      }

      burst.addEventListener('animationend', event => {
        if (event.target === burst) return;
        event.target.remove();
        if (!burst.childElementCount) burst.remove();
      });
      this.layer.appendChild(burst);
      this.later(() => burst.remove(), 950);

      const marker = [...this.root.querySelectorAll('[data-cl-reward-id]')].find(item => item.dataset.clRewardId === reward.id);
      if (marker) {
        marker.dataset.level = String(level);
        marker.classList.add('is-bursting');
        this.later(() => marker.classList.remove('is-bursting'), 600);
      }
    }
  }

  const update = (root, snapshot) => {
    for (const [id, controller] of controllers) {
      if (!controller.root.isConnected || (id === root.id && controller.root !== root)) {
        controller.clearEffects();
        controllers.delete(id);
      }
    }
    if (!root.isConnected || !root.querySelector('[data-cl-reward-effects]')) return;
    let controller = controllers.get(root.id);
    if (!controller) {
      controller = new RewardEffects(root);
      controllers.set(root.id, controller);
    }
    controller.update(snapshot);
  };

  const hydrate = () => {
    document.querySelectorAll('[data-cl-cart-root]').forEach(root => {
      if (root.clRewardSnapshot) update(root, root.clRewardSnapshot);
    });
  };

  window.CLCartRewardEffects = { update };
  motionQuery.addEventListener('change', () => {
    controllers.forEach(controller => {
      if (controller.reduced) controller.clearEffects();
    });
  });
  document.addEventListener('shopify:section:unload', event => {
    controllers.forEach((controller, id) => {
      if (event.target.contains(controller.root) || id === `CLCustomCart-${event.detail?.sectionId}`) {
        controller.clearEffects();
        controllers.delete(id);
      }
    });
  });
  document.addEventListener('shopify:section:load', hydrate);
  hydrate();
})();
